/**
 * Random provides advanced randomness API.
 * @see sekati.math.MathBase for a generic random().
 * @see sekati.math.Range for more range based random methods.
 * @see sekati.math.RandomSeed for a seeded random API.
 */
export class Random {

    /**
     * Returns a random integer between 0-1 exclusive.
     */
    public static randBit(): number {
        return Math.random();
    }

    /**
     * Returns a random hexidecimal color.
     */
    public static hexColor(): number {
        return Number("0x" + Math.floor(Math.random() * 16777215).toString(16).toUpperCase());
    }

    /**
     * Returns a random float.
     * @example <listing version="3.0">
     * Random.float(50); // returns a number between 0-50 exclusive
     * Random.float(20,50); // returns a number between 20-50 exclusive
     * </listing>
     */
    public static float(min: number, max: number = NaN): number {
        if (isNaN(max)) {
            max = min;
            min = 0;
        }
        return Random.randBit() * (max - min) + min;
    }

    /**
     * Return a "tilted" random Boolean value.
     * @param chance        Percentage chance advantage of true.
     * @example <listing version="3.0">
     * Random.boolean(); // returns 50% chance of true.
     * Random.boolean(.75); // returns 75% chance of true.
     * </listing>
     */
    public static boolean(chance: number = 0.5): boolean {
        return (Random.randBit() < chance);
    }

    /**
     * Return a "tilted" value of 1 or -1.
     * @param chance        Percentage chance advantage of true.
     * @example <listing version="3.0">
     * Random.sign(); // returns 50% chance of 1.
     * Random.sign(.75); // returns 75% chance of 1.
     * </listing>
     */
    public static sign(chance: number = 0.5): number {
        return (Random.randBit() < chance) ? 1 : -1;
    }

    /**
     * Return a "tilted" value of 1 or 0.
     * @param chance        Percentage chance advantage of true.
     * @example <listing version="3.0">
     * Random.bit(); // returns 50% chance of 1.
     * Random.bit(.75); // returns 75% chance of 1.
     * </listing>
     */
    public static bit(chance: number = 0.5): number {
        return (Random.randBit() < chance) ? 1 : 0;
    }

    /**
     * Return a random integer.
     * @param chance        Percentage chance advantage of true.
     * @example <listing version="3.0">
     * Random.integer(25); // returns an integer between 0-24 inclusive.
     * Random.integer(10,25); // returns an integer between 10-24 inclusive.
     * </listing>
     */
    public static integer(min: number, max: number = NaN): number {
        if (isNaN(max)) {
            max = min;
            min = 0;
        }
        // Need to use floor instead of bit shift to work properly with negative values:
        return Math.floor(Random.float(min, max));
    }

    /**
     * Random Static Constructor
     */
    constructor() {
        throw new Error("Random is a static class and cannot be instantiated.");
    }
}
